import { useState, useMemo } from 'react'
import merlinImg from '../assets/merlin.png'
import PricingModal from '../components/PricingModal'
import { useAuth } from '../context/AuthContext'
import { Button, Sparkles } from '../components/ui'

export default function WaitlistPage({ step = 'plan', onSignOut }) {
  const [showPricing, setShowPricing] = useState(false)
  const { isAdmin, isSimulating, isSimulatingUsage, setSimulatedPlan, setSimulatedUsagePct } = useAuth()
  const isStuckInSimulation = isAdmin && (isSimulating || isSimulatingUsage)

  // stable sparkle positions for one render
  const sparkleSeed = useMemo(() => Math.random(), [])

  return (
    <div className="min-h-screen onboarding-bg flex items-center justify-center p-4 overflow-hidden relative">
      {/* Admin escape hatch — visible only to admin when simulation active */}
      {isStuckInSimulation && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[950] animate-fade">
          <div className="flex items-center gap-2 px-4 py-2 rounded-full shadow-xl text-xs font-semibold text-white"
            style={{ background: 'linear-gradient(135deg, #f59e0b, #fe8204)', boxShadow: '0 8px 32px rgba(245,158,11,0.4)' }}>
            <span aria-hidden>🎭</span>
            <span>Simulation active</span>
            <button onClick={() => { setSimulatedPlan(null); setSimulatedUsagePct(null); setTimeout(() => location.reload(), 100) }}
              className="ml-1 px-2.5 py-1 rounded-full text-xs font-bold bg-white transition-all hover:scale-105"
              style={{ color: '#b45309' }}>
              Arrêter
            </button>
          </div>
        </div>
      )}

      {/* Ambient decoration */}
      <Sparkles count={14} color="#fe8204" />
      <div className="welcome-orb welcome-orb-1" />
      <div className="welcome-orb welcome-orb-2" />

      <div className="w-full max-w-md relative z-10 welcome-fade-in text-center" key={sparkleSeed}>
        {/* Merlin mascot */}
        <div className="merlin-aura merlin-float inline-block mb-5">
          <img src={merlinImg} alt="Merlin" className="w-28 h-28 object-contain relative z-10"
            style={{ filter: 'drop-shadow(0 0 30px rgba(254,130,4,0.35)) brightness(1.1)' }} />
        </div>

        <h1 className="merlin-title text-4xl font-extrabold tracking-tight mb-1">Merlin</h1>
        <p className="text-xs uppercase tracking-[0.25em] mb-6" style={{ color: 'rgba(255,255,255,0.35)' }}>
          Contenu viral sur-mesure
        </p>

        {/* Card */}
        <div className="merlin-welcome-bubble p-7 welcome-bubble-enter">

          {/* ── STEP: PENDING — signed up, waiting for admin validation ── */}
          {step === 'pending' && (
            <>
              <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{ background: 'rgba(254,130,4,0.15)', border: '1px solid rgba(254,130,4,0.3)' }}>
                <span className="text-2xl" aria-hidden>✨</span>
              </div>

              <h2 className="text-xl font-bold mb-2 leading-tight" style={{ color: 'rgba(255,255,255,0.95)' }}>
                En attente de validation
              </h2>
              <p className="text-sm leading-relaxed mb-3" style={{ color: 'rgba(255,255,255,0.6)' }}>
                Ton compte est bien créé. Merlin est en beta privée, chaque inscription est validée à la main.
              </p>
              <p className="text-sm leading-relaxed mb-5" style={{ color: 'rgba(255,255,255,0.45)' }}>
                Aucune action de ton côté, rien à payer pour l'instant. Repasse dans quelques heures pour vérifier — on valide en continu.
              </p>

              {/* Status pill */}
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full mb-5"
                style={{ background: 'rgba(254,130,4,0.08)', border: '1px solid rgba(254,130,4,0.25)' }}>
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#fe8204' }} />
                <span className="text-xs font-semibold" style={{ color: '#fe8204' }}>Validation en cours</span>
              </div>

              <div className="space-y-2">
                <Button variant="primary" size="lg" fullWidth onClick={() => window.location.reload()}>
                  Vérifier mon accès
                </Button>
                <button onClick={onSignOut}
                  className="w-full py-2 text-xs rounded-xl transition-all hover:bg-white/5"
                  style={{ color: 'rgba(255,255,255,0.35)' }}>
                  Se déconnecter
                </button>
              </div>
            </>
          )}

          {/* ── STEP: PLAN — admin approved but didn't grant a plan, user must pay ── */}
          {step === 'plan' && (
            <>
              <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{ background: 'rgba(22,163,74,0.15)', border: '1px solid rgba(22,163,74,0.3)' }}>
                <span className="text-2xl" aria-hidden>✓</span>
              </div>

              <h2 className="text-xl font-bold mb-2 leading-tight" style={{ color: 'rgba(255,255,255,0.95)' }}>
                Ton accès est validé
              </h2>
              <p className="text-sm leading-relaxed mb-5" style={{ color: 'rgba(255,255,255,0.55)' }}>
                Il te reste juste à choisir un plan pour commencer à générer tes premiers scripts.
              </p>

              {/* Plans preview — beta pricing with crossed-out regular price */}
              <p className="text-[10px] uppercase tracking-widest text-center mb-2" style={{ color: 'rgba(254,130,4,0.7)' }}>
                ★ Tarifs beta — figés tant que tu restes abonné
              </p>
              <div className="grid grid-cols-3 gap-2 mb-6">
                {[
                  { name: 'Basic',    price: '39€',  regular: '69€',  credits: '3 000',  highlight: false },
                  { name: 'Créateur', price: '79€',  regular: '149€', credits: '10 000', highlight: true },
                  { name: 'Pro',      price: '249€', regular: '499€', credits: '50 000', highlight: false },
                ].map(p => (
                  <div key={p.name} className="rounded-xl p-3 text-center transition-all relative overflow-hidden"
                    style={{
                      background: p.highlight ? 'rgba(254,130,4,0.1)' : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${p.highlight ? 'rgba(254,130,4,0.35)' : 'rgba(255,255,255,0.08)'}`,
                    }}>
                    {p.highlight && (
                      <span className="absolute top-0 right-0 text-xs font-bold px-1.5 py-0.5 rounded-bl-md"
                        style={{ background: 'linear-gradient(135deg, #fe8204, #f59e0b)', color: 'white' }}>
                        ★
                      </span>
                    )}
                    <p className="text-xs font-bold" style={{ color: p.highlight ? '#fe8204' : 'rgba(255,255,255,0.75)' }}>{p.name}</p>
                    <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>{p.credits} crédits</p>
                    {p.regular && (
                      <p className="text-[10px] line-through mt-1" style={{ color: 'rgba(255,255,255,0.3)' }}>{p.regular}</p>
                    )}
                    <p className="text-base font-bold" style={{ color: '#fe8204' }}>
                      {p.price}<span className="text-xs font-normal" style={{ color: 'rgba(255,255,255,0.35)' }}>/mois</span>
                    </p>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <Button variant="primary" size="lg" fullWidth onClick={() => setShowPricing(true)}>
                  ✨ Choisir mon plan
                </Button>
                <button onClick={onSignOut}
                  className="w-full py-2 text-xs rounded-xl transition-all hover:bg-white/5"
                  style={{ color: 'rgba(255,255,255,0.35)' }}>
                  Se déconnecter
                </button>
              </div>
            </>
          )}

          {/* ── STEP: REJECTED — admin declined the signup ── */}
          {step === 'rejected' && (
            <>
              <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)' }}>
                <span className="text-2xl" aria-hidden>✕</span>
              </div>

              <h2 className="text-xl font-bold mb-2 leading-tight" style={{ color: 'rgba(255,255,255,0.95)' }}>
                Inscription non retenue
              </h2>
              <p className="text-sm leading-relaxed mb-3" style={{ color: 'rgba(255,255,255,0.6)' }}>
                Merci pour ton intérêt pour Merlin. Cette fois, on n'a pas pu valider ton accès à la beta privée.
              </p>
              <p className="text-sm leading-relaxed mb-5" style={{ color: 'rgba(255,255,255,0.45)' }}>
                Si tu penses que c'est une erreur ou que tu veux discuter, écris-nous à l'adresse ci-dessous. Sinon, on te recontactera à l'ouverture publique.
              </p>

              <div className="space-y-2">
                <button onClick={onSignOut}
                  className="w-full py-2.5 text-xs font-semibold rounded-xl transition-all"
                  style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  Se déconnecter
                </button>
              </div>
            </>
          )}

          {/* Legacy alias — keep "approval" working for any lingering references */}
          {step === 'approval' && (
            <>
              <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{ background: 'rgba(254,130,4,0.15)', border: '1px solid rgba(254,130,4,0.3)' }}>
                <span className="text-2xl" aria-hidden>✨</span>
              </div>
              <h2 className="text-xl font-bold mb-2 leading-tight" style={{ color: 'rgba(255,255,255,0.95)' }}>
                En attente de validation
              </h2>
              <p className="text-sm leading-relaxed mb-5" style={{ color: 'rgba(255,255,255,0.55)' }}>
                Ton compte est bien créé. On valide les inscriptions à la main pendant la beta.
              </p>
              <div className="space-y-2">
                <Button variant="primary" size="lg" fullWidth onClick={() => window.location.reload()}>Vérifier mon accès</Button>
                <button onClick={onSignOut} className="w-full py-2 text-xs rounded-xl hover:bg-white/5" style={{ color: 'rgba(255,255,255,0.35)' }}>Se déconnecter</button>
              </div>
            </>
          )}
        </div>

        <p className="text-xs mt-5" style={{ color: 'rgba(255,255,255,0.3)' }}>
          Une question ? <a href="mailto:contact@merlin-content-ai.com" className="underline hover:text-primary-500 transition-colors">contact@merlin-content-ai.com</a>
        </p>
      </div>

      {/* Pricing modal */}
      <PricingModal open={showPricing} onClose={() => setShowPricing(false)} />
    </div>
  )
}
